import type { IRogue } from '@/js/services/RogueTypes'

// Basic category meta
type Category = {
  name: string,
  url: string,
  meta?: {
    description?: string,
    title?: string
  }
}

type CategorySEOConfig = {
  canonical: string,
  title: string,
  robots: 'all' | 'nofollow' | 'none' | 'noindex,nofollow',
  description: string,
  pageTitle: string
  next?: string,
  prev?: string
}

export const CHANGE_HEADER = ['bumpertype', 'baruse', 'barweight', 'manufacturer', 'colorstandard', 'rogueseries']
const MARKETING_CATEGORIES = ['/3-ships-free', '/crossfit-equipment', '/brands']

export function getCategoryMetaTags(
  query: Record<string, any>,
  category: Category,
  rogue: IRogue,
  totalPages: number
): CategorySEOConfig {
  const page = parseInt(`${query.page_number || 1}`, 10)
  const url = new URL(rogue.l10n.path + category.url, rogue.baseURL)

  let pageTitle = rogue.__(category.name, undefined, category.name)
  const metaTitle = rogue.__(category.meta?.title || category.name, undefined, category.name)

  let hasFacet = false

  const isMarketingPage = MARKETING_CATEGORIES.some(cat => url.pathname.indexOf(cat) === 0)

  const facetsToCheck = isMarketingPage ? ['cat2', 'cat3', 'cat4'].concat(CHANGE_HEADER) : CHANGE_HEADER

  // Check if we want to update the meta title
  for (let i = 0; i < facetsToCheck.length; i += 1) {
    const val = query[facetsToCheck[i]]
    if (val) {
      const updateTitle = Array.isArray(val) ? val[0] : rogue.__(val, undefined, 'specs')
      if (updateTitle !== null) {
        url.searchParams.set(facetsToCheck[i], updateTitle)
        if (pageTitle.toLowerCase().indexOf(updateTitle.toLowerCase()) === -1) {
          pageTitle = facetsToCheck[i].indexOf('cat') === 0 ? `${pageTitle}: ${updateTitle}` : `${updateTitle} ${pageTitle}`
        }
        hasFacet = true
        break
      }
    }
  }

  if (page > 1 && !hasFacet && !isMarketingPage) {
    url.searchParams.set('page_number', `${page}`)
  }

  const result: CategorySEOConfig = {
    canonical   : url.toString(),
    title       : metaTitle,
    robots      : 'all',
    description : rogue.__(category.meta?.description || '', undefined, category.name),
    pageTitle,
  }

  // Dont index any category page using an array as a query param (multiple colors)
  if (Object.values(query).some(val => Array.isArray(val))) {
    result.robots = 'noindex,nofollow'
  }

  // If there are any query parameters that are not marketing params (like shoe_size), we do not want to index the page
  if (Object.keys(query).filter(q => !['page_number', 'srsltid'].includes(q) && !CHANGE_HEADER.includes(q)).length > 0) {
    result.robots = 'noindex,nofollow'
  }

  // If there are more than two marketing params applied (like Color and Brand) we do not want to index
  if (Object.keys(query).filter(q => CHANGE_HEADER.includes(q)).length > 1) {
    result.robots = 'noindex,nofollow'
  }

  if (!hasFacet && !isMarketingPage) {
    if (page > 1) {
      if (page === 2) {
        url.searchParams.delete('page_number')
      } else {
        url.searchParams.set('page_number', `${page - 1}`)
      }
      result.prev = url.toString()
    }

    if (page < totalPages) {
      url.searchParams.set('page_number', `${page + 1}`)
      result.next = url.toString()
    }
  }

  return result
}
